import {
  containsMaliciousPatterns,
  isValidEmail,
  isValidUUID,
  sanitizeInput,
  sanitizePlaceName,
} from 'src/utils/security';
import { z } from 'zod';

const sanitizedStringBase = z.string().transform((val) => sanitizeInput(val));

export const sanitizedString = sanitizedStringBase.refine((val) => !containsMaliciousPatterns(val), {
  message: 'Invalid input detected',
});

export const emailSchema = z
  .string()
  .transform((val) => {
    const sanitized = sanitizeInput(val.trim().toLowerCase());
    return sanitized === '' ? null : sanitized;
  })
  .nullable()
  .refine((val) => val === null || isValidEmail(val), { message: 'Invalid email format' })
  .refine((val) => val === null || !containsMaliciousPatterns(val), { message: 'Invalid input detected' });

export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password must not exceed 128 characters');

export const strongPasswordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password must not exceed 128 characters')
  .refine((val) => /[A-Z]/.test(val), { message: 'Password must contain at least one uppercase letter' })
  .refine((val) => /[a-z]/.test(val), { message: 'Password must contain at least one lowercase letter' })
  .refine((val) => /[0-9]/.test(val), { message: 'Password must contain at least one number' })
  .refine((val) => /[^A-Za-z0-9]/.test(val), { message: 'Password must contain at least one special character' });

export const uuidSchema = z.string().refine((val) => isValidUUID(val), { message: 'Invalid ID format' });

export const phoneSchema = z
  .string()
  .transform((val) => {
    const sanitized = sanitizeInput(val.trim());
    return sanitized === '' ? null : sanitized;
  })
  .nullable()
  .refine((val) => val === null || /^\+?[\d\s\-()]{7,20}$/.test(val), { message: 'Invalid phone number format' })
  .refine((val) => val === null || !containsMaliciousPatterns(val), { message: 'Invalid input detected' });

export const nameSchema = z
  .string()
  .min(1, 'Name is required')
  .max(255, 'Name must not exceed 255 characters')
  .transform((val) => sanitizeInput(val.trim()))
  .refine((val) => !containsMaliciousPatterns(val), { message: 'Invalid input detected' });

export const textFieldSchema = z
  .string()
  .max(1000, 'Text must not exceed 1000 characters')
  .transform((val) => {
    const sanitized = sanitizeInput(val.trim());
    return sanitized === '' ? null : sanitized;
  })
  .nullable()
  .refine((val) => val === null || !containsMaliciousPatterns(val), { message: 'Invalid input detected' });

export const jurisdictionSchema = z
  .string()
  .max(500, 'Jurisdiction must not exceed 500 characters')
  .transform((val) => {
    const sanitized = sanitizePlaceName(val.trim());
    return sanitized === '' ? null : sanitized;
  })
  .nullable()
  .refine((val) => val === null || !containsMaliciousPatterns(val), { message: 'Invalid input detected' });

export const longTextSchema = z
  .string()
  .max(10000, 'Text must not exceed 10000 characters')
  .transform((val) => {
    const sanitized = sanitizeInput(val.trim());
    return sanitized === '' ? null : sanitized;
  })
  .nullable()
  .refine((val) => val === null || !containsMaliciousPatterns(val), { message: 'Invalid input detected' });

export const veryLongTextSchema = z
  .string()
  .max(100000, 'Text must not exceed 100000 characters')
  .transform((val) => sanitizeInput(val))
  .refine((val) => !containsMaliciousPatterns(val), { message: 'Invalid input detected' });

export const urlSchema = z
  .string()
  .url('Invalid URL format')
  .transform((val) => sanitizeInput(val))
  .refine((val) => !containsMaliciousPatterns(val), { message: 'Invalid input detected' });

const isoDateSchema = z
  .string()
  .transform((val) => {
    const trimmed = val.trim();
    return trimmed === '' ? null : trimmed;
  })
  .nullable()
  .refine((val) => val === null || /^\d{4}-\d{2}-\d{2}$/.test(val), { message: 'Invalid date format (YYYY-MM-DD)' });

export const clientSchema = z.object({
  name: nameSchema,
  email: emailSchema.optional().nullable(),
  phone: phoneSchema.optional().nullable(),
  jurisdiction: jurisdictionSchema.optional().nullable(),
  status: z.enum(['active', 'prospect', 'inactive', 'pending']).optional().nullable(),
  client_type: z.enum(['individual', 'organization']).optional().nullable(),
  address_street: textFieldSchema.optional().nullable(),
  address_city: textFieldSchema.optional().nullable(),
  address_state: textFieldSchema.optional().nullable(),
  address_postal: textFieldSchema.optional().nullable(),
  address_country: textFieldSchema.optional().nullable(),
  practice_area: textFieldSchema.optional().nullable(),
  opposing_party: longTextSchema.optional().nullable(),
  referral_source: textFieldSchema.optional().nullable(),
  engagement_date: isoDateSchema.optional().nullable(),
  law_firm_id: uuidSchema.optional().nullable(),
  profile_id: uuidSchema,
});

export const createClientSchema = clientSchema.omit({ profile_id: true });
export const updateClientSchema = createClientSchema.partial();

export const contractSchema = z.object({
  name: nameSchema,
  client_id: uuidSchema.optional().nullable(),
  entity_id: uuidSchema,
  file_path: textFieldSchema.optional().nullable(),
  file_name: nameSchema.optional().nullable(),
  status: z.enum(['pending', 'reviewing', 'completed', 'archived']).optional(),
  notes: longTextSchema.optional().nullable(),
});

export const researchSchema = z.object({
  research_name: nameSchema,
  jurisdiction: jurisdictionSchema,
  client_id: uuidSchema.optional().nullable(),
  entity_id: uuidSchema,
  research_type: z.enum(['case_law', 'statute', 'regulation', 'general']).optional(),
  query: longTextSchema,
});

export const signUpSchema = z.object({
  email: emailSchema,
  password: strongPasswordSchema,
  firstName: nameSchema,
  lastName: nameSchema,
  title: textFieldSchema.optional(),
  phone: phoneSchema.optional(),
  jurisdiction: jurisdictionSchema.optional(),
});

export const loginSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
});

export const contactFormSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  phone: phoneSchema.optional(),
  company: nameSchema.optional(),
  message: longTextSchema,
});

export const profileSchema = z.object({
  first_name: nameSchema,
  last_name: nameSchema,
  email: emailSchema,
  phone: phoneSchema.optional().nullable(),
  title: textFieldSchema.optional().nullable(),
  jurisdiction: jurisdictionSchema.optional().nullable(),
  firm_id: uuidSchema.optional().nullable(),
  custom_fields: z.record(z.unknown()).optional().nullable(),
  newsletter_opt_in: z.boolean().optional().nullable(),
});

export const updateProfileSchema = profileSchema.partial();

export const clientNotesSchema = z.object({
  client_id: uuidSchema,
  content: longTextSchema,
  title: nameSchema.optional().nullable(),
});

export const crmContactSchema = z.object({
  first_name: nameSchema,
  last_name: nameSchema,
  email: emailSchema.optional().nullable(),
  phone: phoneSchema.optional().nullable(),
  mobile: phoneSchema.optional().nullable(),
  title: textFieldSchema.optional().nullable(),
  department: textFieldSchema.optional().nullable(),
  account_id: uuidSchema.optional().nullable(),
  address: textFieldSchema.optional().nullable(),
  city: textFieldSchema.optional().nullable(),
  state: textFieldSchema.optional().nullable(),
  postal_code: textFieldSchema.optional().nullable(),
  country: textFieldSchema.optional().nullable(),
  notes: longTextSchema.optional().nullable(),
  tags: z.array(z.string()).optional().nullable(),
  custom_fields: z.record(z.unknown()).optional().nullable(),
});

export const accountSchema = z.object({
  name: nameSchema,
  industry: textFieldSchema.optional().nullable(),
  website: urlSchema.optional().nullable(),
  phone: phoneSchema.optional().nullable(),
  address: textFieldSchema.optional().nullable(),
  city: textFieldSchema.optional().nullable(),
  state: textFieldSchema.optional().nullable(),
  postal_code: textFieldSchema.optional().nullable(),
  country: textFieldSchema.optional().nullable(),
  notes: longTextSchema.optional().nullable(),
  domain: textFieldSchema.optional().nullable(),
  size: z.enum(['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+']).optional().nullable(),
  region: textFieldSchema.optional().nullable(),
  status: z.enum(['active', 'inactive', 'prospect', 'churned']).optional().nullable(),
  tags: z.array(z.string()).optional().nullable(),
  custom_fields: z.record(z.unknown()).optional().nullable(),
});

export const opportunitySchema = z.object({
  name: nameSchema,
  account_id: uuidSchema.optional().nullable(),
  contact_id: uuidSchema.optional().nullable(),
  stage: z.enum(['Prospecting', 'Qualification', 'Proposal', 'Negotiation', 'Closed Won', 'Closed Lost']).optional(),
  amount: z.number().min(0).optional().nullable(),
  currency: z.string().max(3).optional().nullable(),
  probability: z.number().min(0).max(100).optional().nullable(),
  close_date: z.string().optional().nullable(),
  source: textFieldSchema.optional().nullable(),
  next_step: textFieldSchema.optional().nullable(),
  forecast_category: z.enum(['pipeline', 'best_case', 'commit', 'closed']).optional().nullable(),
  notes: longTextSchema.optional().nullable(),
  custom_fields: z.record(z.unknown()).optional().nullable(),
});

export const emailTemplateSchema = z.object({
  name: nameSchema,
  subject: textFieldSchema,
  body: veryLongTextSchema,
  category: textFieldSchema.optional().nullable(),
  tags: z.array(z.string()).optional().nullable(),
  is_active: z.boolean().optional(),
});

export type ClientInput = z.infer<typeof clientSchema>;
export type CreateClientInput = z.infer<typeof createClientSchema>;
export type UpdateClientInput = z.infer<typeof updateClientSchema>;
export type ContractInput = z.infer<typeof contractSchema>;
export type ResearchInput = z.infer<typeof researchSchema>;
export type SignUpInput = z.infer<typeof signUpSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type ContactFormInput = z.infer<typeof contactFormSchema>;
export type ProfileInput = z.infer<typeof profileSchema>;
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;
export type ClientNotesInput = z.infer<typeof clientNotesSchema>;
export type CRMContactInput = z.infer<typeof crmContactSchema>;
export type AccountInput = z.infer<typeof accountSchema>;
export type OpportunityInput = z.infer<typeof opportunitySchema>;
export type EmailTemplateInput = z.infer<typeof emailTemplateSchema>;
